import { MailOutlined } from "@ant-design/icons";
import { api } from "@common/api";
import { estabrookApi } from "@common/estabrookApi";
import { getCurrentSeason } from "@common/season";
import { InputMask } from "@components/atoms/InputMask/InputMask";
import { useAntiBotProtection } from "@hooks";
import { Button, Col, Form, Input, message, Row } from "antd";
import { useState } from "react";
import { useNavigate } from 'react-router-dom';

interface ContactFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  serviceAddress: string;
  website?: string; // Honeypot field
}

export const LandingPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showThankyou, setShowThankyou] = useState(false);
  const { handleFieldFocus, handleFieldBlur, validateHumanBehavior, resetProtection, formRef } = useAntiBotProtection();

  const onFinish = async (values: ContactFormValues) => {
    if (values.website) { // Honeypot check
      message.error("An error occurred. Please try again later.");
      return;
    }

    const humanCheck = validateHumanBehavior();
    if (!humanCheck.isValid) {
      message.error("Please interact with the form naturally before submitting.");
      return;
    }

    setLoading(true);
    try {
      const { website, ...cleanValues } = values;

      // The phone field holds a masked US value, e.g. "(415) 555-2671".
      // estabrook-api validates phoneNumber with @IsPhoneNumber() and no region,
      // which only accepts E.164 - so send "+14155552671" rather than the mask.
      const phoneDigits = cleanValues.phone.replace(/\D/g, '');
      const phoneE164 = phoneDigits.length === 10 ? `+1${phoneDigits}` : `+${phoneDigits}`;

      const leadData = {
        firstName: cleanValues.firstName,
        lastName: cleanValues.lastName,
        email: cleanValues.email,
        phone: cleanValues.phone,
        serviceAddress: cleanValues.serviceAddress,
        source: 'Website Landing Page',
      };
      await api.post('/customer-leads', leadData);

      // Also send to Estabrook's own system (estabrook-api)
      try {
        await estabrookApi.post('/api/v1/public/leads', {
          firstName: cleanValues.firstName,
          lastName: cleanValues.lastName,
          email: cleanValues.email,
          phoneNumber: phoneE164,
          address: cleanValues.serviceAddress,
        });
      } catch (estabrookError) {
        console.error('Error sending lead to estabrook-api:', estabrookError);
      }

      const salesforceUrl = 'https://go.estabrookinc.com/l/1013982/2023-02-17/4fr';
      const iframe = document.createElement('iframe');
      iframe.name = 'hidden-salesforce-frame';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const salesforceForm = document.createElement('form');
      salesforceForm.method = 'POST';
      salesforceForm.action = salesforceUrl;
      salesforceForm.target = 'hidden-salesforce-frame';
      salesforceForm.style.display = 'none';
      salesforceForm.acceptCharset = 'UTF-8';

      const addField = (name: string, value: string) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        salesforceForm.appendChild(input);
      };

      addField('email', cleanValues.email);
      addField('First-Name', cleanValues.firstName);
      addField('Last-Name', cleanValues.lastName);
      addField('phone', phoneDigits);
      addField('Address', cleanValues.serviceAddress);
      addField('source', 'Website Landing Page');
      addField('honeyPotCheck', 'completed');


      document.body.appendChild(salesforceForm);
      salesforceForm.submit();

      setTimeout(() => {
        document.body.removeChild(salesforceForm);
        document.body.removeChild(iframe);
      }, 1000);

      // Continue with the normal flow - we do not need to wait for the Salesforce response
      setShowThankyou(true);
      message.success('Your message has been sent successfully!');
      form.resetFields();
      resetProtection();
    } catch (error) {
      console.error('Error sending message:', error);
      message.error('Unable to send your message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="bg-dark flex flex-col lg:flex-row">
        <div className="lg:min-h-screen relative">
          <img src="/assets/images/estabrook-home-pic.png" alt="Estabrook Home" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="bg-white lg:max-w-[40%] flex items-center justify-center p-12 lg:p-6 pt-10">
          <div className="w-full" ref={formRef}>
            <h1 className="font-azonix !text-black text-center text-xl md:text-3xl uppercase tracking-wider mb-6 md:mb-12">
              Now Accepting Customers for {getCurrentSeason()} Season
            </h1>
            {showThankyou ? (
              <div className="text-center py-10">
                <h2 className="font-azonix text-2xl mb-4 text-primary">Thank You!</h2>
                <p className="text-lg">We have received your request and will contact you soon.</p>
              </div>
            ) : (
              <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="firstName" label={<span className="text-charcoal">First Name</span>} rules={[{ required: true, message: "Please enter your first name" }]}>
                      <div><Input placeholder="First Name" onFocus={() => handleFieldFocus('firstName')} onBlur={() => handleFieldBlur('firstName')} /></div>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="lastName" label={<span className="text-charcoal">Last Name</span>} rules={[{ required: true, message: "Please enter your last name" }]}>
                      <div><Input placeholder="Last Name" onFocus={() => handleFieldFocus('lastName')} onBlur={() => handleFieldBlur('lastName')} /></div>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="email" label={<span className="text-charcoal">Email Address</span>} rules={[{ required: true, message: "Please enter your email" }]}>
                  <div><Input placeholder="Email Address" onFocus={() => handleFieldFocus('email')} onBlur={() => handleFieldBlur('email')} /></div>
                </Form.Item>
                <Form.Item name="phone" label={<span className="text-charcoal">Phone Number</span>} rules={[{ required: true, message: "Please enter your phone number" }]}>
                  <InputMask mask="usphone" placeholder="000 000-0000" />
                </Form.Item>
                <Form.Item name="serviceAddress" label={<span className="text-charcoal">Service Address</span>} rules={[{ required: true, message: "Please enter your address" }]}>
                  <div><Input.TextArea rows={3} placeholder="Service Address" onFocus={() => handleFieldFocus('serviceAddress')} onBlur={() => handleFieldBlur('serviceAddress')} /></div>
                </Form.Item>
                <div className="text-dark text-xs mt-4 mb-6">*ALL FIELDS MUST BE COMPLETED</div>
                <div className="text-right my-6">
                  <Button htmlType="submit" loading={loading} block className="btn-double-cut !w-fit">SUBMIT</Button>
                </div>

                {/* Hidden honeypot field for anti-spam protection */}
                <Form.Item name="website" style={{ display: 'none' }}>
                  <Input />
                </Form.Item>
              </Form>
            )}
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="min-h-[400px] md:h-1/2 bg-cover bg-center relative" style={{ backgroundImage: `url('/assets/images/background-darker.png')` }}>
        <div className="bg-black/10 absolute inset-0" />
        <div className="relative h-full flex items-center p-8">
          <div className="max-w-4xl">
            <h2 className="text-primary text-4xl uppercase tracking-wider my-6">About Estabrook Inc.</h2>
            <p className="text-light text-lg text-justify tracking-wider mb-6">Founded in 2022 by the Estabrook family and the Ruppert brothers, Estabrook Inc. has quickly earned a reputation for reliable, high-quality snow removal in Tahoe Donner. Backed by decades of local experience and a fleet of high-horsepower New Holland tractors built to handle the harshest Sierra storms, we've brought innovation to the industry with our custom-built app, SNOWSYNC—giving homeowners real-time updates and a new level of transparency. With a growing base of dedicated customers, Estabrook is setting a new standard for snow removal in the Tahoe region.</p>
            <Button block className="btn-double-cut !w-fit" onClick={() => { navigate('/about'); window.scrollTo(0, 0); }}>
              Learn More
            </Button>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <section className="w-full bg-dark py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-black p-8 text-center">
            <MailOutlined className="text-primary text-4xl mb-2" />
            <div className="font-azonix text-white text-xl uppercase tracking-wider mb-3">Email Us</div>
            <div className="text-secondary text-base tracking-wider mb-1">contact@estabrookinc.com</div>
            <div className="text-secondary text-base tracking-wider">We respond within 24 hours</div>
          </div>
        </div>
      </section>
    </div>
  );
};
